import { AREAS_DE_PLANTA, type Campos, type PersonalDeHoja } from "@gestiondoc/shared";
import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useTRPC } from "../api.ts";
import { mensajeDe } from "../utiles.ts";
import { Aviso, Boton } from "./ui.tsx";

/**
 * "¿Quiénes están hoy?" (2026-10-28): en la hoja de higiene todos los días van casi los mismos
 * nombres y áreas. Quien la llena tiene su lista guardada; desmarca a quien faltó, agrega a
 * quien haga falta y la app escribe nombre y área en las filas de la tabla, sin huecos. La hoja
 * sigue en blanco hasta que pulsa "Poner en la hoja"; lo demás (palomitas, N/A, fecha, hora,
 * firma) sigue siendo con el lápiz. Pensado para la tablet: controles de 44 px.
 */

type PersonaDelDia = { clave: number; nombre: string; area: string; presente: boolean };

export type DatosDelPersonal = {
  /** Cuántas filas tiene la tabla de personal del formato. */
  caben: number;
  /** La lista guardada de esta persona para este formato (con los que suelen faltar). */
  lista: PersonalDeHoja;
  /** Quiénes están puestos en la hoja ahora mismo. */
  puestos: PersonalDeHoja;
};

export function PersonalDelDia({
  hojaId,
  inicial,
  antes,
  onPuesto,
}: {
  hojaId: string;
  inicial: DatosDelPersonal;
  /** Guarda la tinta pendiente antes de tocar la hoja. */
  antes?: (() => Promise<void>) | undefined;
  onPuesto: (r: { campos: Campos; guardadaEn: Date }) => void;
}) {
  const trpc = useTRPC();
  const idLista = useId();
  const [filas, setFilas] = useState<PersonaDelDia[]>(() => filasIniciales(inicial));
  const [puestos, setPuestos] = useState<PersonalDeHoja>(inicial.puestos);
  const [abierto, setAbierto] = useState(inicial.puestos.length === 0);
  const [error, setError] = useState<string | null>(null);
  const poner = useMutation(
    trpc.hojas.ponerPersonal.mutationOptions({
      onSuccess: (r, variables) => {
        setPuestos(
          variables.personal
            .filter((p) => p.presente)
            .map((p) => ({ nombre: p.nombre, area: p.area ?? "" })),
        );
        setAbierto(false);
        setError(null);
        onPuesto(r);
      },
      onError: (e) => setError(mensajeDe(e)),
    }),
  );

  const conNombre = filas.filter((f) => f.nombre.trim());
  const presentes = conNombre.filter((f) => f.presente);
  const sobran = presentes.length > inicial.caben;
  const areas = [
    ...new Set([...AREAS_DE_PLANTA, ...filas.map((f) => f.area.trim()).filter(Boolean)]),
  ];

  function cambiar(clave: number, cambio: Partial<PersonaDelDia>) {
    setFilas((fs) => fs.map((f) => (f.clave === clave ? { ...f, ...cambio } : f)));
  }
  function agregar() {
    setFilas((fs) => [...fs, filaVacia()]);
  }
  function quitar(clave: number) {
    setFilas((fs) => (fs.length > 1 ? fs.filter((f) => f.clave !== clave) : [filaVacia()]));
  }
  async function ponerEnLaHoja() {
    await antes?.();
    poner.mutate({
      id: hojaId,
      personal: conNombre.map((f) => ({
        nombre: f.nombre.trim(),
        area: f.area.trim(),
        presente: f.presente,
      })),
    });
  }

  if (!abierto) {
    return (
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-tarjeta border border-borde bg-white px-4 py-3">
        <span className="text-[13px] font-semibold text-marino">Personal de hoy</span>
        <span className="text-[13px] text-texto-2">
          {puestos.length === 0
            ? "Nadie puesto en la hoja."
            : `${puestos.length} en la hoja: ${puestos.map((p) => p.nombre).join(", ")}.`}
        </span>
        <Boton variante="secundario" className="ml-auto" onClick={() => setAbierto(true)}>
          Cambiar
        </Boton>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-tarjeta border border-borde bg-white px-4 py-3">
      <div className="mb-1 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="text-[13px] font-semibold text-marino">¿Quiénes están hoy?</span>
        <span className="text-[12px] text-texto-3">
          Desmarca a quien faltó y agrega a quien haga falta. La lista se guarda para la próxima
          hoja; la hoja sigue en blanco hasta que la pongas.
        </span>
      </div>
      {error && <Aviso className="my-2">{error}</Aviso>}
      <datalist id={idLista}>
        {areas.map((a) => (
          <option key={a} value={a} />
        ))}
      </datalist>
      <ul className="mt-2 flex flex-col gap-1.5">
        {filas.map((f, i) => (
          <li key={f.clave} className="flex items-center gap-2">
            <label
              className="grid h-11 w-11 shrink-0 place-items-center rounded-lg hover:bg-fondo"
              title={f.presente ? "Está hoy" : "Faltó hoy"}
            >
              <input
                type="checkbox"
                checked={f.presente}
                onChange={(e) => cambiar(f.clave, { presente: e.target.checked })}
                aria-label={`${f.nombre.trim() || "Esta persona"} está hoy`}
                className="size-5 accent-marino"
              />
            </label>
            <input
              type="text"
              value={f.nombre}
              placeholder="Nombre"
              aria-label="Nombre"
              autoComplete="off"
              onChange={(e) => cambiar(f.clave, { nombre: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter" && i === filas.length - 1) {
                  e.preventDefault();
                  agregar();
                }
              }}
              className={`${ENTRADA} min-w-0 flex-[2] ${f.presente ? "" : "text-texto-3 line-through"}`}
            />
            <input
              type="text"
              value={f.area}
              placeholder="Área"
              aria-label="Área"
              autoComplete="off"
              list={idLista}
              onChange={(e) => cambiar(f.clave, { area: e.target.value })}
              className={`${ENTRADA} min-w-0 flex-1 ${f.presente ? "" : "text-texto-3"}`}
            />
            <button
              type="button"
              aria-label={`Quitar a ${f.nombre.trim() || "esta persona"} de la lista`}
              title="Quitar de la lista"
              onClick={() => quitar(f.clave)}
              className="grid h-11 w-11 shrink-0 place-items-center rounded-lg text-texto-3 hover:bg-rojo-fondo hover:text-rojo"
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Boton variante="secundario" className="min-h-[44px]" onClick={agregar}>
          + Agregar persona
        </Boton>
        <span
          className={`ml-auto text-[12px] ${sobran ? "font-semibold text-rojo" : "text-texto-3"}`}
        >
          {sobran
            ? `Caben ${inicial.caben} en la hoja; para el resto abre otra hoja`
            : `${presentes.length} de ${inicial.caben} filas`}
        </span>
        {puestos.length > 0 && (
          <Boton
            variante="secundario"
            className="min-h-[44px]"
            onClick={() => {
              setFilas(filasIniciales({ ...inicial, puestos }));
              setAbierto(false);
            }}
          >
            Cancelar
          </Boton>
        )}
        <Boton
          className="min-h-[44px]"
          disabled={presentes.length === 0 || sobran || poner.isPending}
          onClick={() => void ponerEnLaHoja()}
        >
          {poner.isPending ? "Poniendo…" : "Poner en la hoja"}
        </Boton>
      </div>
    </div>
  );
}

const ENTRADA =
  "min-h-[44px] rounded-md border border-borde bg-fondo px-3 text-[13.5px] outline-none focus:border-marino";

let siguienteClave = 1;
function filaVacia(): PersonaDelDia {
  return { clave: siguienteClave++, nombre: "", area: "", presente: true };
}

/**
 * La lista guardada, marcando como presentes a los que ya están en la hoja (o a todos, si la
 * hoja está en blanco); los puestos que no estén en la lista se suman. Sin lista ni puestos, una
 * fila vacía para empezar a escribir.
 */
function filasIniciales(d: DatosDelPersonal): PersonaDelDia[] {
  const enHoja = new Set(d.puestos.map((p) => p.nombre));
  const filas = d.lista.map((p) => ({
    ...filaVacia(),
    ...p,
    presente: d.puestos.length === 0 || enHoja.has(p.nombre),
  }));
  const enLista = new Set(d.lista.map((p) => p.nombre));
  for (const p of d.puestos) {
    if (!enLista.has(p.nombre)) filas.push({ ...filaVacia(), ...p, presente: true });
  }
  return filas.length ? filas : [filaVacia()];
}
